import { supabase } from "@/integrations/supabase/client";
import { checkPlanLimits } from "@/lib/planLimits";
import { voiceAgentService, VoiceAgent, VoiceAgentCreateData } from "@/services/voiceAgentService";
import { Tables } from "@/types/database.types";

// Use database type for voice agents
type VoiceTemplateAgent = Tables<'voice_agents'>;

// Cache for voice template agent data
interface VoiceTemplateCache {
  timestamp: number;
  data: VoiceTemplateAgent[];
}

// Cache storage with 5-minute expiration
let voiceTemplateAgentCache: VoiceTemplateCache | null = null;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Cache for API calls with specific search parameters
const recentSearchCache: Map<
  string,
  {
    timestamp: number;
    result: {
      data?: VoiceTemplateAgent[];
      error?: unknown;
    };
  }
> = new Map();

// Type guard for VoiceTemplateAgent
function isVoiceTemplateAgent(obj: any): obj is VoiceTemplateAgent {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.id === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.agent_name === 'string' &&
    typeof obj.organization_id === 'string' &&
    typeof obj.created_by === 'string'
  );
}

/**
 * Fetch voice template agents
 */
export const fetchVoiceTemplateAgents = async (
  params: { is_template?: boolean; search?: string; persona_id?: string; industry_id?: string } = {
    is_template: true,
  }
) => {
  try {
    // If we're fetching all templates (no search/filters) and we have a valid cache, use it
    const isBasicFetch =
      params.is_template === true && !params.search && !params.persona_id && !params.industry_id;
    if (
      isBasicFetch &&
      voiceTemplateAgentCache &&
      Date.now() - voiceTemplateAgentCache.timestamp < CACHE_EXPIRY_MS
    ) {
      console.log("[voiceTemplateService] Using cached voice template agents");
      return { data: voiceTemplateAgentCache.data };
    }

    // Check for recent identical search in cache
    const cacheKey = JSON.stringify(params);
    const cachedSearch = recentSearchCache.get(cacheKey);
    if (cachedSearch && Date.now() - cachedSearch.timestamp < CACHE_EXPIRY_MS) {
      console.log("[voiceTemplateService] Using cached search result");
      return cachedSearch.result;
    }

    // Build query
    let query = supabase
      .from("voice_agents")
      .select(`
        *,
        industries (id, name, emoji),
        personas (id, avatar_name, type),
        organizations (id, name)
      `)
      .eq("is_template", params.is_template || true)
      .order("created_at", { ascending: false });

    // Add filters
    if (params.search) {
      query = query.ilike("title", `%${params.search}%`);
    }
    if (params.persona_id) {
      query = query.eq("persona_id", params.persona_id);
    }
    if (params.industry_id) {
      query = query.eq("industry_id", params.industry_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching voice template agents:", error);
      return { error };
    }

    const result = { data: data || [] };

    // Cache the result
    recentSearchCache.set(cacheKey, {
      timestamp: Date.now(),
      result,
    });

    // If this is a basic fetch, update the template cache
    if (isBasicFetch) {
      voiceTemplateAgentCache = {
        timestamp: Date.now(),
        data: data || [],
      };
    }

    // Clean up old cache entries
    if (recentSearchCache.size > 20) {
      const now = Date.now();
      for (const [key, value] of recentSearchCache.entries()) {
        if (now - value.timestamp > CACHE_EXPIRY_MS) {
          recentSearchCache.delete(key);
        }
      }
    }

    return result;
  } catch (error: unknown) {
    console.error("Error fetching voice template agents:", error);
    return { error };
  }
};

/**
 * Clear the cache - useful when templates might have been updated
 */
export const clearVoiceTemplateCache = () => {
  voiceTemplateAgentCache = null;
  recentSearchCache.clear();
  console.log("[voiceTemplateService] Voice template cache cleared");
};

/**
 * Get voice template by ID
 */
export const getVoiceTemplateById = async (templateId: string) => {
  try {
    const { data, error } = await supabase
      .from("voice_agents")
      .select(`
        *,
        industries (id, name, emoji),
        personas (id, avatar_name, type),
        organizations (id, name)
      `)
      .eq("id", templateId)
      .eq("is_template", true)
      .single();

    if (error) {
      console.error("Error fetching voice template by ID:", error);
      return { error };
    }

    return { data };
  } catch (error: unknown) {
    console.error("Error fetching voice template by ID:", error);
    return { error };
  }
};

/**
 * Update voice template
 */
export const updateVoiceTemplate = async (
  templateId: string,
  templateData: Partial<VoiceTemplateAgent>
) => {
  try {
    const { data, error } = await supabase
      .from("voice_agents")
      .update({
        ...templateData,
        updated_at: new Date().toISOString(),
      })
      .eq("id", templateId)
      .eq("is_template", true)
      .select()
      .single();

    if (error) {
      console.error("Error updating voice template:", error);
      return { error };
    }

    // Clear cache since template was updated
    clearVoiceTemplateCache();

    return { data };
  } catch (error: unknown) {
    console.error("Error updating voice template:", error);
    return { error };
  }
};

/**
 * Duplicate voice template to create a new voice agent
 */
export const duplicateVoiceTemplateToAgent = async (
  templateId: string,
  userId: string,
  organizationId: string
): Promise<{ success: boolean; agentId?: string; error?: string; data?: VoiceAgent }> => {
  try {
    console.log(`[duplicateVoiceTemplateToAgent] Starting duplication of voice template ${templateId}`);

    // Get the current user to access their email and organization data
    const [userResult, orgResult] = await Promise.all([
      supabase.auth.getUser(),
      supabase
        .from("organizations")
        .select("name, industry_id")
        .eq("id", organizationId)
        .single()
    ]);
    
    const userEmail = userResult.data.user?.email || "";
    const organization = orgResult.data;
    
    if (orgResult.error) {
      console.warn(`[duplicateVoiceTemplateToAgent] Could not fetch organization data: ${orgResult.error.message}`);
    }

    // Get the voice template
    const { data: template, error: templateError } = await supabase
      .from("voice_agents")
      .select("*")
      .eq("id", templateId)
      .eq("is_template", true)
      .single();

    if (templateError) throw new Error(`Voice template fetch failed: ${templateError.message}`);
    if (!template) throw new Error("Voice template not found");

    console.log(`[duplicateVoiceTemplateToAgent] Found voice template: ${template.title}`);

    // Get persona information if template has a persona_id
    let persona = null;
    if (template.persona_id) {
      const { data: personaData, error: personaError } = await supabase
        .from("personas")
        .select("avatar_name, avatar_image_url")
        .eq("id", template.persona_id)
        .single();
      
      if (!personaError && personaData) {
        persona = personaData;
        console.log(`[duplicateVoiceTemplateToAgent] Found persona: ${persona.avatar_name}`);
      }
    }

    // Get associated form fields and behaviors in parallel
    const [formFieldsResult, behaviorsResult] = await Promise.all([
      supabase
        .from("form_fields")
        .select("*")
        .eq("agent_id", templateId),
      supabase
        .from("behaviors")
        .select("*")
        .eq("agent_id", templateId)
    ]);

    // Check for errors in parallel fetches
    if (formFieldsResult.error) {
      throw new Error(`Form fields fetch failed: ${formFieldsResult.error.message}`);
    }
    if (behaviorsResult.error) {
      throw new Error(`Behaviors fetch failed: ${behaviorsResult.error.message}`);
    }

    const formFields = formFieldsResult.data || [];
    const behaviors = behaviorsResult.data || [];

    console.log(`[duplicateVoiceTemplateToAgent] Found ${formFields.length} form fields and ${behaviors.length} behaviors`);

    // Create new voice agent from template
    const newVoiceAgentData: VoiceAgentCreateData = {
      // Basic info
      title: template.title,
      agent_name: persona?.avatar_name || template.agent_name,
      description: template.description,
      
      // Template relationships
      template_source_id: templateId,
      
      // Voice and behavior settings
      instructions: template.instructions,
      first_message: template.first_message,
      end_call_message: template.end_call_message,
      ai_temperature: template.ai_temperature,
      voice_provider: template.voice_provider,
      voice_id: template.voice_id,
      max_duration_seconds: template.max_duration_seconds,
      silence_timeout_seconds: template.silence_timeout_seconds,
      first_message_interruptions_enabled: template.first_message_interruptions_enabled,
      background_sound_enabled: template.background_sound_enabled,
      voicemail_message: template.voicemail_message,
      
      // Visual elements
      avatar_url: persona?.avatar_image_url || template.avatar_url,
      
      // Organization and user
      organization_id: organizationId,
      created_by: userId,
      notification_email: userEmail,
    };

    // Create the voice agent using the service
    const { data: newAgent, error: createError } = await voiceAgentService.createVoiceAgent(newVoiceAgentData);

    if (createError) throw createError;
    if (!newAgent) throw new Error("No voice agent data returned");

    console.log(`[duplicateVoiceTemplateToAgent] Created voice agent: ${newAgent.id}`);

    // Copy form fields if any exist
    if (formFields.length > 0) {
      const formFieldsToInsert = formFields.map(field => ({
        voice_agent_id: newAgent.id, // Use voice_agent_id for voice agents
        agent_id: null, // Explicitly set agent_id to null
        title: field.title,
        instructions: field.instructions,
        is_required: field.is_required,
        position: field.position,
        field_type: field.field_type,
        answer_type: field.answer_type,
        options: field.options,
      }));

      const { error: formFieldsError } = await supabase
        .from("form_fields")
        .insert(formFieldsToInsert);

      if (formFieldsError) {
        console.error("Error copying form fields:", formFieldsError);
        // Don't fail the whole operation, just log the error
      } else {
        console.log(`[duplicateVoiceTemplateToAgent] Copied ${formFields.length} form fields`);
      }
    }

    // Copy behaviors if any exist
    if (behaviors.length > 0) {
      const behaviorsToInsert = behaviors.map(behavior => ({
        voice_agent_id: newAgent.id, // Use voice_agent_id for voice agents
        agent_id: null, // Explicitly set agent_id to null
        title: behavior.title,
        description: behavior.description,
        comment: behavior.comment,
        is_active: behavior.is_active,
        organization_id: organizationId,
      }));

      const { error: behaviorsError } = await supabase
        .from("behaviors")
        .insert(behaviorsToInsert);

      if (behaviorsError) {
        console.error("Error copying behaviors:", behaviorsError);
        // Don't fail the whole operation, just log the error
      } else {
        console.log(`[duplicateVoiceTemplateToAgent] Copied ${behaviors.length} behaviors`);
      }
    }

    console.log(`[duplicateVoiceTemplateToAgent] Successfully duplicated voice template ${templateId} to voice agent ${newAgent.id}`);

    return {
      success: true,
      agentId: newAgent.id,
      data: newAgent
    };

  } catch (error) {
    console.error("[duplicateVoiceTemplateToAgent] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};

/**
 * Filter voice templates by selection (persona and industry)
 * Reuses the same filtering logic as regular templates
 */
export const filterVoiceTemplatesBySelection = (
  allTemplates: VoiceTemplateAgent[],
  selectedPersona: { id: string; avatar_name?: string | null; type?: string | null } | null,
  organizationIndustryId: string | null
) => {
  if (!selectedPersona) return [];

  // For "Create from scratch" - no templates needed
  if (selectedPersona.id === "custom") {
    return [];
  }

  // For "More templates" - show templates with no persona
  if (selectedPersona.id === "more_templates") {
    const noPersonaTemplates = allTemplates.filter(t => !t.persona_id);
    
    // Priority: org industry first, then industry = null
    const industryMatches = noPersonaTemplates.filter(t => 
      t.industry_id === organizationIndustryId
    );
    const otherTemplates = noPersonaTemplates.filter(t => 
      !t.industry_id
    );
    
    return [...industryMatches, ...otherTemplates];
  }

  // For specific persona - show templates with matching persona_id
  const personaTemplates = allTemplates.filter(t => t.persona_id === selectedPersona.id);
  
  // Priority: org industry first, then industry = null (fallback)
  const industryMatches = personaTemplates.filter(t => 
    t.industry_id === organizationIndustryId
  );
  const fallbackTemplates = personaTemplates.filter(t => 
    !t.industry_id
  );
  
  return [...industryMatches, ...fallbackTemplates];
};

/**
 * Create a new voice template
 */
export const createVoiceTemplate = async (
  templateData: Partial<VoiceTemplateAgent> & {
    title: string;
    agent_name: string;
    instructions: string;
    first_message: string;
    voice_provider: string;
    voice_id: string;
    created_by: string;
    organization_id: string;
  }
) => {
  try {
    const { data, error } = await supabase
      .from("voice_agents")
      .insert({
        ...templateData,
        is_template: true,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating voice template:", error);
      return { error };
    }

    // Clear cache since a new template was created
    clearVoiceTemplateCache();

    return { data };
  } catch (error: unknown) {
    console.error("Error creating voice template:", error);
    return { error };
  }
};

/**
 * Delete voice template
 */
export const deleteVoiceTemplate = async (templateId: string, organizationId: string) => {
  try {
    const { error } = await supabase
      .from("voice_agents")
      .delete()
      .eq("id", templateId)
      .eq("organization_id", organizationId)
      .eq("is_template", true);

    if (error) {
      console.error("Error deleting voice template:", error);
      return { error };
    }

    // Clear cache since template was deleted
    clearVoiceTemplateCache();

    return { success: true };
  } catch (error: unknown) {
    console.error("Error deleting voice template:", error);
    return { error };
  }
}; 